<script setup lang="ts">
    import { reactive, inject, onMounted, computed } from 'vue';
    import SurveysList from './List.vue';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import { loadGlobalTemplates, createSurveyFromPrevious, createSurveyFromGlobalTemplate } from './Surveys.access';
    import { type Survey, type GlobalTemplate } from './SurveysTypes';
    import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { Permissions } from '@shared/Root/Permissions';
    import { ContextAccess } from '@shared/Root/Context.access';
    import List from '@shared/components/List.vue';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';
    import { bsAlert } from '@shared/components/bsAlerts';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { useRoute } from 'vue-router';
    import AiCreateSurvey from '@cwd/AiTools/AiCreateSurvey.vue';
    import NewPreview from './NewPreview.vue';
    import { UserEventService } from '@shared/Root/UserEvent.service';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);
    const route = useRoute();
    const showAllGlobalTemplates = 'all';
    const newSurveyQueryString = '?newsurvey=true';
    const userEventService = injectorAccess.injector.get(UserEventService);

    const state = reactive<{
        hasTemplatesScope: boolean | undefined;
        hasSurveysScope: boolean | undefined;
        hideFreeTemplates: boolean | undefined;
        globalTemplates: GlobalTemplate[];
        isLoading: boolean;
        currentGlobalTemplateTagId: string;
        surveyId: string | null;
        isGlobalTemplate: boolean;
        eventId: string | undefined;
        showAiModal: boolean;
    }>({
        hasTemplatesScope: undefined,
        hasSurveysScope: undefined,
        hideFreeTemplates: undefined,
        globalTemplates: [],
        isLoading: false,
        currentGlobalTemplateTagId: showAllGlobalTemplates,
        surveyId: null,
        isGlobalTemplate: false,
        eventId: undefined,
        showAiModal: false,
    });

    onMounted(async () => {
        const requireTemplatesScopeParams: vRequireScopeParams = {
            Feature: 'SurveyTemplates',
            Permission: Permissions.Read,
        };

        state.hasTemplatesScope = scopeAccess.hasScope(requireTemplatesScopeParams);

        const requireSurveysScopeParams: vRequireScopeParams = {
            Feature: 'Surveys',
            Permission: Permissions.Write,
        };

        state.hasSurveysScope = scopeAccess.hasScope(requireSurveysScopeParams);

        state.hideFreeTemplates = contextAccess.state.currentAccount!.AccountSettings.find((s) => s.Key === 'Survey.HideFreeTemplates')?.Value === 'true';
        state.hasTemplatesScope && !state.hideFreeTemplates && onLoadGlobalTemplates();

        state.eventId = route.query.eventId as string;
    });

    const onLoadGlobalTemplates = async () => {
        if (state.isLoading) return;
        state.isLoading = true;

        await loadGlobalTemplates()
            .then((data: GlobalTemplate[]) => {
                state.globalTemplates = data.sort((a, b) => b.SortOrder - a.SortOrder);
            })
            .finally(() => (state.isLoading = false));
    };

    const createNewSurvey = async () => {
        if (state.isLoading || !state.surveyId) return;
        state.isLoading = true;

        if (state.isGlobalTemplate) {
            await createSurveyFromGlobalTemplate(state.surveyId, state.eventId)
                .then((data: Survey) => {
                    userEventService.registerUserEvent('Survey', 'Created draft survey', data.Title);
                    document.location = '/Surveys/' + data.SurveyId + newSurveyQueryString;
                })
                .catch(() => {
                    bsAlert({ content: $Resources.ErrorCouldNotCloneSurvey, type: 'danger' });
                    state.isLoading = false;
                });
        } else {
            await createSurveyFromPrevious(state.surveyId, state.eventId)
                .then((data: Survey) => {
                    userEventService.registerUserEvent('Survey', 'Created draft survey', data.Title);
                    document.location = '/Surveys/' + data.SurveyId + newSurveyQueryString;
                })
                .catch(() => {
                    bsAlert({ content: $Resources.ErrorCouldNotCloneSurvey, type: 'danger' });
                    state.isLoading = false;
                });
        }
    };

    const filteredGlobalTemplates = computed(() => {
        if (state.currentGlobalTemplateTagId === showAllGlobalTemplates) return state.globalTemplates;

        return state.globalTemplates.filter((item) => {
            const found = item.Tags.find((tag) => tag.TagId === state.currentGlobalTemplateTagId);
            return found ? true : false;
        });
    });

    const globalTemplateTags = computed(() => {
        const tags: { display: string; key: string }[] = [{ display: $Resources.ToggleOptionsAll, key: showAllGlobalTemplates }];
        const tagKeys = new Set<string>();

        state.globalTemplates.forEach((template) => {
            template.Tags.forEach((tag) => {
                if (!tagKeys.has(tag.TagId)) {
                    tagKeys.add(tag.TagId);
                    tags.push({
                        display: tag.Title,
                        key: tag.TagId,
                    });
                }
            });
        });

        return tags;
    });

    const iframeSrc = computed(() => {
        if (!state.surveyId) return '';
        return combineUrlSegments(contextAccess.state.currentAccount!.PublicEventUrl.PublicHost, 
            'Surveys', '' + state.surveyId, '?preview=true');
    });

    const setSurveyId = (surveyId: string, isGlobalTemplate?: boolean) => {
        if (state.isLoading) return;
        state.isGlobalTemplate = isGlobalTemplate || false;
        state.surveyId = surveyId === state.surveyId ? null : surveyId;
    };

    const surveyAiTools = computed(() => {
        let culture = 'sv';
        let language = 'Swedish';
        if (contextAccess.state.currentUser?.Language?.Culture) {
            culture = contextAccess.state.currentUser.Language.LanguageId;
            language = contextAccess.state.currentUser.Language.Name;
        }
        
        return {
            show: state.showAiModal,
            selectedSurveyId: state.surveyId || '',
            surveyType: state.isGlobalTemplate ? 'Template' : 'Survey',
            culture: culture,
            language: language,
        };
    });
</script>

<script lang="ts"></script>

<template>
    <header role="banner" class="p-lg-r p-lg-l">
        <div class="d-flex justify-content-between align-items-baseline">
            <h1>{{ $Resources.NewSurveyFromTemplateHeading }}</h1>
        </div>

        <p class="m-lg-b">
            {{ $Resources.NewSurveyFromTemplateIntroduction }}
        </p>
    </header>

    <tabs v-if="state.hasTemplatesScope !== undefined && state.hideFreeTemplates !== undefined" navClass="tabs list-unstyled m-lg-r m-lg-l">
        <tab v-if="state.hasTemplatesScope" :title="$Resources.TemplatesTab">
            <SurveysList :onlyTemplates="true" :selectedId="state.surveyId" @clickedSurveyId="(newVal) => setSurveyId(newVal)"></SurveysList>
        </tab>
        <tab v-if="!state.hideFreeTemplates" :title="$Resources.FreeTemplatesTab">
            <List
                itemId="SurveyId"
                displayAs="cards"
                :items="filteredGlobalTemplates"
                :toggleOptions="globalTemplateTags"
                :filterKeys="['Title', 'Description']"
                :loading="state.isLoading"
                :selectedId="state.surveyId"
                :selectedToggle="state.currentGlobalTemplateTagId"
                @update:selectedToggle="(newVal) => (state.currentGlobalTemplateTagId = newVal)">
                <template #item="{ item }">
                    <div @click="setSurveyId(item.SurveyId, true)" role="button">
                        <div class="card-media">
                            <img :src="item.ImageUrl" :alt="item.Title" />
                        </div>
                        <div class="card-content">
                            <h2 class="h4">
                                <a href="javascript:void(0)" role="button">
                                    {{ item.Title }}
                                </a>
                            </h2>
                            <p>{{ item.Description }}</p>
                        </div>
                    </div>
                </template>
            </List>
        </tab>
        <tab :title="$Resources.SurveysAsTemplatesTab">
            <SurveysList :hideTemplates="true" :selectedId="state.surveyId" @clickedSurveyId="(newVal) => setSurveyId(newVal)"></SurveysList>
        </tab>
    </tabs>

    <NewPreview :iframeSrc="iframeSrc" @close="state.surveyId = null">
        <template #toolbar>
            <template v-if="state.hasSurveysScope">
                <!-- Previous safe-guard for creating surveys with AI: && featureAccess.hasFeature('BackOffice') -->
                <button
                    v-if="featureAccess.hasFeature('AIAssistant.CreateSurvey')"
                    @click="state.showAiModal = true"
                    type="button"
                    class="btn btn-default btn-flex"
                    :disabled="state.isLoading">
                    <svg
                        class="c-primary"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round">
                        <rect x="6" y="6" width="12" height="12" />
                        <path d="M14,6" />
                        <rect x="5426.4" y="4330.8" width="8" height="8" />
                        <circle cx="5439.4" cy="4334.8" r="2" />
                        <line x1="5434.4" y1="4334.8" x2="5437.4" y2="4334.8" />
                        <path d="M5432.4,4330.8" />
                        <circle cx="5424.4" cy="4325.8" r="2" />
                        <circle cx="5436.4" cy="4325.8" r="2" />
                        <circle cx="5436.4" cy="4343.8" r="2" />
                        <circle cx="5424.4" cy="4343.8" r="2" />
                        <circle cx="5421.4" cy="4334.8" r="2" />
                        <line x1="5426.4" y1="4334.8" x2="5423.4" y2="4334.8" />
                        <path d="M5426.2,4326.7l1.1,0.6c0.7,0.3,1.1,1,1.1,1.8v1.7" />
                        <path d="M5434.6,4326.7l-1.1,0.6c-0.7,0.3-1.1,1-1.1,1.8v1.7" />
                        <path d="M5434.6,4342.9l-1.1-0.6c-0.7-0.3-1.1-1-1.1-1.8v-1.7" />
                        <path d="M5426.2,4342.9l1.1-0.6c0.7-0.3,1.1-1,1.1-1.8v-1.7" />
                        <line x1="10" y1="2" x2="10" y2="6" />
                        <line x1="14" y1="2" x2="14" y2="6" />
                        <line x1="10" y1="18" x2="10" y2="22" />
                        <line x1="14" y1="18" x2="14" y2="22" />
                        <line x1="22" y1="10" x2="18" y2="10" />
                        <line x1="22" y1="14" x2="18" y2="14" />
                        <line x1="6" y1="10" x2="2" y2="10" />
                        <line x1="6" y1="14" x2="2" y2="14" />
                        <rect x="10" y="10" width="4" height="4" />
                    </svg>
                    {{ $Resources.AIAssistantCreateSurvey }}
                </button>

                <button type="button" class="btn btn-cta p-lg-r p-lg-l" @click="createNewSurvey" :disabled="state.isLoading">
                    {{ $Resources.StartEditingBtn }}
                    <i class="fa fa-chevron-right" aria-hidden="true"></i>
                </button>
            </template>
        </template>
    </NewPreview>

    <AiCreateSurvey :createSurvey="surveyAiTools" @close="state.showAiModal = false"></AiCreateSurvey>
</template>

<style scoped lang="less"></style>

<style lang="less"></style>
